import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, func, number, oneOf, string } from 'prop-types'

import { TENANT_COM } from 'shared/consts'
import Stars from 'views/components/molecules/StarRating/Stars'
import { ICON_SIZE } from 'views/consts'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withDataQA from 'views/enhancers/withDataQA'
import './StarRating.scss'

import { MAX_RATING, RATING_TEXT_ID, SELLER_MODAL_POSTFIX } from './consts'

/* eslint-disable react/prop-types */
const RatingLabel = ({ value }) => {
  const {
    publicRuntimeConfig: {
      publicConfig: { locale },
    },
  } = useShopAndRuntimeConfig()

  const rating = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
  return (
    <span suppressHydrationWarning className="a-visuallyhidden">
      <Trans
        id="general.label.product.ratingOutOfMax"
        values={{
          rating,
          maxRating: MAX_RATING,
        }}
      />
    </span>
  )
}

const renderStatic = ({ rating, iconSize, className }) => (
  <>
    <RatingLabel value={rating} />
    <ul className={className}>
      <Stars {...{ rating, iconSize }} />
    </ul>
  </>
)

const renderInteractive = ({ iconSize, className }) => (
  <>
    <ul className={className}>
      <Stars rating={0} iconSize={iconSize} />
    </ul>
    <ul className={`${className} m-StarRating__stars--interactive`}>
      <Stars rating={MAX_RATING} iconSize={iconSize} />
    </ul>
  </>
)

const renderClickable = ({ rating, iconSize, onClick, className }) => (
  <>
    <RatingLabel value={rating} />
    <ul className={className}>
      <Stars rating={rating} iconSize={iconSize} onClick={onClick} />
    </ul>
  </>
)

const renderQuestionLink = ({
  answeredQuestionsCount,
  hideDelimiter,
  href,
  anchorLinks,
  verbose,
  isSponsored,
}) => {
  const Component = href ? 'a' : 'span'
  const componentProps = {
    href,
    className:
      'm-StarRating__qna-locator l-flex__fixed a-link u-vertical-align--with-stars u-no-wrap',
  }

  if (anchorLinks) {
    componentProps['data-clientside-hook'] = 'anchorLink'
  }

  if (href && isSponsored) {
    componentProps.rel = 'nofollow noopener'
  }

  if (answeredQuestionsCount > 0) {
    const answeredQuestionsText = verbose ? (
      <Trans
        id="general.label.product.numberOfQuestions"
        values={{ numberOfQuestions: answeredQuestionsCount }}
      />
    ) : (
      `(${answeredQuestionsCount})`
    )

    return (
      <>
        {!hideDelimiter && (
          <span className="m-StarRating__rating-delimiter l-flex__fixed u-vertical-align--with-stars u-margin-x-small--left u-margin-x-small--right">
            |
          </span>
        )}
        <Component {...componentProps}>
          {answeredQuestionsText}
          {!verbose && (
            <span className="a-visuallyhidden">
              <Trans id="general.label.product.numberOfQuestions" />
            </span>
          )}
        </Component>
      </>
    )
  }
  return null
}

const renderRatingCount = ({
  ratingCount,
  verbose,
  noParentheses,
  ratingCountColor,
}) => {
  if (typeof ratingCount !== 'undefined' && ratingCount != null) {
    let ratingCountText
    if (verbose) {
      ratingCountText = (
        <Trans
          id="general.label.product.numberOfRatings"
          values={{ numberOfRatings: ratingCount }}
        />
      )
    } else {
      ratingCountText = noParentheses ? ratingCount : `(${ratingCount})`
    }

    const className = classNames(
      'u-vertical-align--with-stars u-no-wrap m-StarRating__rating-count-text',
      {
        [`u-color--${ratingCountColor}`]: ratingCountColor,
      }
    )

    return (
      <span className={className}>
        {ratingCountText}
        {!verbose && (
          <span className="a-visuallyhidden">
            <Trans id="general.label.product.numberOfRatings" />
          </span>
        )}
      </span>
    )
  }
  return null
}

const renderNonProductRatingCount = ratingCount => (
  <div className="u-font-weight--bold m-StarRating__rating-count">
    <Trans
      id="general.label.numberOfRatings"
      values={{ numberOfRatings: ratingCount }}
    />
  </div>
)

const renderTextContent = ({
  interactive,
  componentClassName,
  ratingCount,
  verbose,
  noParentheses,
  href,
  hrefFaq,
  answeredQuestionsCount,
  hideReviews,
  anchorLinks,
  product,
  ratingCountColor,
  crosssellClickTarget,
  isSponsored,
}) => {
  const Component = href ? 'a' : 'span'
  const extendedClassName = `m-StarRating__reviews-locator ${componentClassName}`
  const componentProps = {
    href,
    className: extendedClassName,
  }

  if (crosssellClickTarget) {
    componentProps['data-crosssell-click-target'] = crosssellClickTarget
  }

  if (anchorLinks) {
    componentProps['data-clientside-hook'] = 'anchorLink'
  }

  if (href && isSponsored) {
    componentProps.rel = 'nofollow noopener'
  }

  return (
    <>
      {!hideReviews && !interactive && (
        <Component {...componentProps}>
          {product
            ? renderRatingCount({
                ratingCount,
                verbose,
                noParentheses,
                ratingCountColor,
              })
            : renderNonProductRatingCount(ratingCount)}
        </Component>
      )}
      {renderQuestionLink({
        answeredQuestionsCount,
        hideDelimiter: hideReviews,
        href: hrefFaq,
        anchorLinks,
        verbose,
        isSponsored,
      })}
    </>
  )
}

/* eslint-enable react/prop-types */

const StarRating = props => {
  const {
    alignToEnd,
    answeredQuestionsCount,
    centered,
    centeredDesktopOnly,
    centeredMobileOnly,
    centeredTinyOnly,
    className,
    hasSuperScript,
    hideReviews,
    href: hrefSrc,
    hrefFaq,
    iconSize,
    inline,
    interactive,
    invisible,
    isMasterPage,
    isSellerModal,
    isSellerRatingList,
    isSponsored,
    noParentheses,
    onClick,
    product,
    rating,
    ratingCount,
    ratingCountColor,
    crosssellClickTarget,
    showAverage,
    verbose,
  } = props

  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const href = hrefSrc && !invisible ? hrefSrc : null

  const starClassName = classNames(
    'm-StarRating l-flex l-flex--wrap',
    {
      'l-flex--center': centered,
      'l-flex--inline': inline,
      'l-flex--end@medium l-flex--vertical-stretch u-text-align--right@medium':
        alignToEnd,
      'm-StarRating__invisible': invisible,
      'l-flex--center@small-tiny': centeredMobileOnly && !centered,
      'l-flex--center@medium': centeredDesktopOnly && !centered,
      'l-flex--center@tiny-only': centeredTinyOnly && !centered,
    },
    className
  )

  const componentClassName = classNames('l-flex__fixed', 'l-flex', {
    'a-link': href || (isSellerModal && !isSellerRatingList),
  })
  const textContentClass = classNames('m-StarRating__text-container', {
    'l-flex l-flex--wrap l-flex--end@medium': alignToEnd,
  })
  const StarWrapperComponent = href ? 'a' : 'span'

  const wrapperComponentProps = {
    className: componentClassName,
  }

  if (crosssellClickTarget) {
    wrapperComponentProps['data-crosssell-click-target'] = crosssellClickTarget
  }

  if (!isSellerModal) {
    Object.assign(wrapperComponentProps, { href })
  }

  const anchorLinks = href && href.charAt(0) === '#'

  if (anchorLinks) {
    wrapperComponentProps['data-clientside-hook'] = 'anchorLink'
  }

  if (href && isSponsored) {
    wrapperComponentProps.rel = 'nofollow noopener'
  }

  const textContent = (
    <div className={textContentClass}>
      {renderTextContent({
        interactive,
        componentClassName,
        ratingCount,
        verbose,
        noParentheses,
        href,
        hrefFaq,
        answeredQuestionsCount,
        hideReviews,
        anchorLinks,
        product,
        ratingCountColor,
        crosssellClickTarget,
        isSellerModal,
        isSponsored,
      })}
    </div>
  )

  const hasRatingCount = ratingCount !== null

  const starsClassName = classNames('m-StarRating__stars a-list-reset', {
    'm-StarRating__stars--alignToEnd': alignToEnd,
    'u-margin-x-small--right': hasRatingCount,
    'l-flex': !showAverage,
    'l-flex--inline': showAverage,
  })

  const starRatingId =
    isSellerModal && !isSellerRatingList
      ? `${RATING_TEXT_ID}${SELLER_MODAL_POSTFIX}`
      : RATING_TEXT_ID

  let starComponent

  if (interactive || (onClick && !href)) {
    starComponent = interactive
      ? renderInteractive({ iconSize, className: starsClassName })
      : renderClickable({
          rating,
          iconSize,
          onClick,
          className: starsClassName,
        })
  } else {
    starComponent = renderStatic({
      rating,
      iconSize,
      className: starsClassName,
    })
  }

  return (
    <div
      className={starClassName}
      data-qa-id="product-rating"
      id={starRatingId}
      aria-hidden
    >
      <div className="l-flex m-StarRating__stars__wrapper">
        <StarWrapperComponent {...wrapperComponentProps}>
          {starComponent}
          {showAverage ? (
            <span className="m-StarRating__rating-average">
              <Trans
                id="general.label.product.ratingOutOfMax"
                values={{
                  rating: (Math.round(rating * 10) / 10).toLocaleString(
                    'de-DE'
                  ),
                  maxRating: MAX_RATING,
                }}
              />
            </span>
          ) : (
            ''
          )}
        </StarWrapperComponent>
        {tenant === TENANT_COM && hasSuperScript && (
          <sup className="m-StarRating__superscript">27</sup>
        )}
      </div>
      {hasRatingCount && !isMasterPage ? textContent : null}
    </div>
  )
}

StarRating.propTypes = {
  alignToEnd: bool,
  answeredQuestionsCount: number,
  centered: bool,
  centeredDesktopOnly: bool,
  centeredMobileOnly: bool,
  centeredTinyOnly: bool,
  className: string,
  hasSuperScript: bool,
  hideReviews: bool,
  href: string,
  hrefFaq: string,
  iconSize: oneOf(ICON_SIZE),
  inline: bool,
  interactive: bool,
  invisible: bool,
  isMasterPage: bool,
  isSellerModal: bool,
  isSellerRatingList: bool,
  noParentheses: bool,
  onClick: func,
  product: bool,
  crosssellClickTarget: string,
  rating: number,
  ratingCount: number,
  ratingCountColor: string,
  showAverage: bool,
  verbose: bool,
  isSponsored: bool,
}

StarRating.defaultProps = {
  alignToEnd: null,
  answeredQuestionsCount: null,
  centered: false,
  centeredDesktopOnly: false,
  centeredMobileOnly: false,
  centeredTinyOnly: false,
  hasSuperScript: false,
  hideReviews: false,
  href: null,
  hrefFaq: null,
  iconSize: 'small',
  inline: null,
  interactive: null,
  invisible: false,
  isMasterPage: false,
  isSellerModal: false,
  isSellerRatingList: false,
  crosssellClickTarget: null,
  noParentheses: null,
  onClick: null,
  product: true,
  rating: null,
  ratingCount: null,
  ratingCountColor: null,
  showAverage: false,
  verbose: null,
  isSponsored: false,
}

export default withDataQA(StarRating)
